import React, { useMemo, useRef, useState } from 'react';
import { AutoComplete, Avatar, Empty, Form, Input, InputRef } from 'antd';
import type { FormInstance } from 'antd';
import { SearchOutlined, CheckOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'utils/moment';
import shopSelectors from 'services/selectors/shop';
import shopActions from 'services/actions/shop';
import { useAppDispatch } from 'store/hooks';
import type { FormValues } from '../services/types';
import type { IStaffUI } from 'types/staff';
import StaffOptionLabel from 'widgets/Staff/StaffOptionLabel';
import { groupedSelectionBorder, toneAvatar, toneFill, useStaffGroupTone } from 'widgets/Staff/useStaffGroupTone';

type StaffListProps = {
  value?: string;
  onChange?: (value: string) => void;
};

const StaffList: React.FC<StaffListProps> = ({ value, onChange }) => {
  const dispatch = useAppDispatch();
  const inputRef = useRef<InputRef>(null);
  const staffs = shopSelectors.staffsAvailable() || [];
  const toneOf = useStaffGroupTone();
  const [searchText, setSearchText] = useState<string>('');
  const form = Form.useFormInstance<FormValues>();

  const fetchHistories = (staffId: string) => {
    if (!staffId) return;
    const date = form.getFieldValue('date');
    
    dispatch(
      shopActions.blockHourHistories.fetch({
        staffId,
        page: 1,
        pageSize: 50,
      })
    );
  };

  const handleSetStaff = (id: string) => {
    onChange?.(id);
    fetchHistories(id);
  };

  const initialOptions = useMemo(
    () =>
      staffs.map((staff) => ({
        value: staff.id,
        name: staff.name,
        keywords: [staff.name?.toLocaleLowerCase()],
        label: <StaffOptionLabel name={staff.name} avatar={staff.avatar} tone={toneOf(staff.id)} />,
      })),
    [staffs, toneOf]
  );

  const options = useMemo(() => {
    if (!searchText) return initialOptions;
    return initialOptions.filter((item) =>
      item.name?.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [initialOptions, searchText]);
  
  const filtered = useMemo(
    () =>
      staffs.filter((staff) =>
        staff.name.toLowerCase().includes(searchText.toLowerCase()) && staff.id !== '0'
      ),
    [staffs, searchText]
  );

  const handleSelect = (value: string) => {
    inputRef.current?.blur();
    setSearchText('');
    const selected = staffs.find((item) => item.id === value);
    if (selected) handleSetStaff(selected.id);
  };

  return (
    <StaffPanel>
      <SearchWrap>
        <AutoComplete
          style={{ width: '100%' }}
          options={options}
          notFoundContent={<Empty description="No staff found" />}
          onSelect={handleSelect}
          value={searchText}
          onChange={setSearchText}
          dropdownMatchSelectWidth={250}
        >
          <StaffSearchInput
            ref={inputRef}
            prefix={<SearchOutlined style={{ color: '#aaa' }} />}
            placeholder="Staff Name"
          />
        </AutoComplete>
      </SearchWrap>
      <StaffGrid>
        {filtered.length ? (
          filtered.map((staff: IStaffUI) => {
            const active = value === staff.id;
            const tone = toneOf(staff.id);
            return (
              <StaffItem
                key={staff.id}
                type="button"
                active={active}
                style={tone && { ...toneFill(tone), ...groupedSelectionBorder(active, '#148CF1') }}
                onClick={() => handleSetStaff(staff.id)}
              >
                <AvatarWrap>
                  <Avatar src={staff.avatar} size={44} style={toneAvatar(tone)}>
                    {staff.name?.[0] || ''}
                  </Avatar>
                  {active && (
                    <AvatarOverlay>
                      <CheckOutlined />
                    </AvatarOverlay>
                  )}
                </AvatarWrap>
                <StaffName $selected={active} style={tone && { color: tone.color }}>{staff.name}</StaffName>
              </StaffItem>
            );
          })
        ) : (
          <Empty description="No staff found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </StaffGrid>
    </StaffPanel>
  );
};

export default StaffList;

const StaffPanel = styled.div`
  width: 260px;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  padding: 12px 8px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 8px
`;

const SearchWrap = styled.div`
  margin-bottom: 12px;
`;

const StaffSearchInput = styled(Input)`
  width: 100%;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  height: 42px;

  .ant-input {
    border: none;
    box-shadow: none;
    background: transparent;
    height: 100%;
  }
`;

const StaffGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  overflow-y: auto;
  flex: 1;
  padding-right: 4px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #d0d0d0; border-radius: 4px; }
`;

const StaffItem = styled.button<{ active: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 10px 8px;
  border-radius: 14px;
  border: 1px solid ${({ active }) => (active ? '#148CF1' : '#fff')};
  background: ${({ active }) => (active ? '#148CF1' : '#fff')};
  box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
  text-align: center;
  &:hover {
    background: ${({ active }) => (active ? '#148CF1' : '#FFEFEB')};
  }
`;

const AvatarWrap = styled.div`
  position: relative;
  width: 56px;
  height: 56px;
`;

const AvatarOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(20, 140, 241, 0.14);
  border-radius: 50%;
  color: #148CF1;
  font-size: 0.9rem;
`;

const StaffName = styled.span<{ $selected: boolean }>`
  font-size: 12px;
  color: ${({ $selected }) => ($selected ? '#fff' : '#1D2129')};
  font-weight: ${({ $selected }) => ($selected ? '600' : '500')};
  text-align: center;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
